import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { ApplyFormData } from '../ApplyFormComponent1';
import { PhoneCodeSelect } from './PhoneCode/PhoneCodeSelect';
import { useLocation } from 'react-router-dom';

const generateRange = (start: number, end: number): number[] =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i);

export const EmergencyContactForm: React.FC = () => {
  const {
    register,
    formState: { errors },
    control,
  } = useFormContext<ApplyFormData>();
  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';

  const years = generateRange(1900, new Date().getFullYear());
  const months = generateRange(1, 12);
  const days = generateRange(1, 31);

  const inputStyles =
    'w-full border border-black mt-1 h-[27px] text-sm px-1 py-0';
  const selectStyles =
    'w-full border border-black mt-1 h-[27px] text-sm px-1 py-0';
  const labelStyles = 'block text-gray-800 text-sm';

  return (
    <div className="w-full max-md:max-w-md max-md:mx-auto bg-[#f6f3e6] mt-5 p-5">
      {/* 姓・名 ブロック */}
      <div className="flex space-x-4 mb-4">
        {/* 姓 */}
        <div className="w-1/2">
          <label className={labelStyles}>
            {useEnglish ? 'Last Name' : '姓'}{' '}
            <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder={useEnglish ? 'e.g. Tanaka' : '例）トウキュウ'}
              className={`${inputStyles} ${errors.emergencyData?.firstName ? 'border-red-500' : ''}`}
              {...register('emergencyData.firstName', {
                required: useEnglish
                  ? 'Last Name is required'
                  : '緊急連絡先の「姓」が未入力です',
              })}
            />
            {errors.emergencyData?.firstName && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                !
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {useEnglish
              ? 'If using Roman letters, please enter in Roman.'
              : '※ローマ字表記の方はローマ字でご入力ください'}
          </p>
          {errors.emergencyData?.firstName && (
            <div className="text-red-500 text-sm mt-1">
              {errors.emergencyData.firstName.message?.toString()}
            </div>
          )}
        </div>

        {/* 名 */}
        <div className="w-1/2">
          <label className={labelStyles}>
            {useEnglish ? 'First Name' : '名'}{' '}
            <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder={useEnglish ? 'e.g. Taro' : '例）タロウ'}
              className={`${inputStyles} ${errors.emergencyData?.lastName ? 'border-red-500' : ''}`}
              {...register('emergencyData.lastName', {
                required: useEnglish
                  ? 'First Name is required'
                  : '緊急連絡先の「名」が未入力です',
              })}
            />
            {errors.emergencyData?.lastName && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                !
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {useEnglish
              ? 'If using Roman letters, please enter in Roman.'
              : '※ローマ字表記の方はローマ字でご入力ください'}
          </p>
          {errors.emergencyData?.lastName && (
            <div className="text-red-500 text-sm mt-1">
              {errors.emergencyData.lastName.message?.toString()}
            </div>
          )}
        </div>
      </div>

      {/* セイ（フリガナ）・メイ（フリガナ） ブロック */}
      <div className="flex space-x-4 mb-4">
        {/* 姓（フリガナ） */}
        <div className="w-1/2">
          <label className={labelStyles}>
            {useEnglish ? 'Last Name (Kana)' : '姓（フリガナ）'}{' '}
            <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder={useEnglish ? 'e.g. TANAKA' : '例）トウキュウ'}
              className={`${inputStyles} ${errors.emergencyData?.firstNameKana ? 'border-red-500' : ''}`}
              {...register('emergencyData.firstNameKana', {
                required: useEnglish
                  ? 'Last Name (Kana) is required'
                  : '緊急連絡先「姓（フリガナ）」が未入力です',
              })}
            />
            {errors.emergencyData?.firstNameKana && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                !
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {useEnglish
              ? 'If using Roman letters, please enter in Roman.'
              : '※ローマ字表記の方はローマ字でご入力ください'}
          </p>
          {errors.emergencyData?.firstNameKana && (
            <div className="text-red-500 text-sm mt-1">
              {errors.emergencyData.firstNameKana.message?.toString()}
            </div>
          )}
        </div>

        {/* 名（フリガナ） */}
        <div className="w-1/2">
          <label className={labelStyles}>
            {useEnglish ? 'First Name (Kana)' : '名（フリガナ）'}{' '}
            <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder={useEnglish ? 'e.g. TARO' : '例）タロウ'}
              className={`${inputStyles} ${errors.emergencyData?.lastNameKana ? 'border-red-500' : ''}`}
              {...register('emergencyData.lastNameKana', {
                required: useEnglish
                  ? 'First Name (Kana) is required'
                  : '緊急連絡先「名（フリガナ）」が未入力です',
              })}
            />
            {errors.emergencyData?.lastNameKana && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                !
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {useEnglish
              ? 'If using Roman letters, please enter in Roman.'
              : '※ローマ字表記の方はローマ字でご入力ください'}
          </p>
          {errors.emergencyData?.lastNameKana && (
            <div className="text-red-500 text-sm mt-1">
              {errors.emergencyData.lastNameKana.message?.toString()}
            </div>
          )}
        </div>
      </div>

      {/* 入居者との続柄 */}
      <div className="mb-4">
        <label className={labelStyles}>
          {useEnglish ? 'Relationship to Resident' : '入居者との続柄'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder={useEnglish ? 'e.g. Parent' : '例）父親'}
            className={`${inputStyles} ${errors.emergencyData?.relation ? 'border-red-500' : ''}`}
            {...register('emergencyData.relation', {
              required: useEnglish
                ? 'Relationship is required'
                : '続柄が未入力です',
            })}
          />
          {errors.emergencyData?.relation && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
              !
            </span>
          )}
        </div>
        {errors.emergencyData?.relation && (
          <div className="text-red-500 text-sm mt-1">
            {errors.emergencyData.relation.message?.toString()}
          </div>
        )}
      </div>

      {/* 現住所 */}
      <div className="mb-4">
        <label className={labelStyles}>
          {useEnglish ? 'Current Address' : '現住所'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder={
              useEnglish
                ? 'e.g. 1-1-1 Shibuya, Tokyo'
                : '例）東京都渋谷区桜丘町31-2'
            }
            className={`${inputStyles} ${errors.emergencyData?.address ? 'border-red-500' : ''}`}
            {...register('emergencyData.address', {
              required: useEnglish
                ? 'Current address is required'
                : '住所が未入力です',
            })}
          />
          {errors.emergencyData?.address && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
              !
            </span>
          )}
        </div>
        {errors.emergencyData?.address && (
          <div className="text-red-500 text-sm mt-1">
            {errors.emergencyData.address.message?.toString()}
          </div>
        )}
      </div>

      {/* 生年月日 */}
      <div className="mb-4">
        <label className={labelStyles}>
          {useEnglish ? 'Birthday' : '生年月日'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="flex space-x-2 mt-1">
          {/* 年 */}
          <div className="relative w-1/3">
            <select
              className={`${selectStyles} ${errors.emergencyData?.birthYear ? 'border-red-500' : ''}`}
              {...register('emergencyData.birthYear', {
                required: useEnglish
                  ? 'Year is required'
                  : '生年月日の年が未入力です',
              })}
            >
              <option value="">{useEnglish ? 'Year' : '年'}</option>
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                  {useEnglish ? '' : '年'}
                </option>
              ))}
            </select>
            {errors.emergencyData?.birthYear && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-2">
                !
              </span>
            )}
          </div>
          {/* 月 */}
          <div className="relative w-1/3">
            <select
              className={`${selectStyles} ${errors.emergencyData?.birthMonth ? 'border-red-500' : ''}`}
              {...register('emergencyData.birthMonth', {
                required: useEnglish
                  ? 'Month is required'
                  : '生年月日の月が未入力です',
              })}
            >
              <option value="">{useEnglish ? 'Month' : '月'}</option>
              {months.map((m) => (
                <option key={m} value={m}>
                  {m}
                  {useEnglish ? '' : '月'}
                </option>
              ))}
            </select>
            {errors.emergencyData?.birthMonth && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-2">
                !
              </span>
            )}
          </div>
          {/* 日 */}
          <div className="relative w-1/3">
            <select
              className={`${selectStyles} ${errors.emergencyData?.birthDay ? 'border-red-500' : ''}`}
              {...register('emergencyData.birthDay', {
                required: useEnglish
                  ? 'Day is required'
                  : '生年月日の日が未入力です',
              })}
            >
              <option value="">{useEnglish ? 'Day' : '日'}</option>
              {days.map((d) => (
                <option key={d} value={d}>
                  {d}
                  {useEnglish ? '' : '日'}
                </option>
              ))}
            </select>
            {errors.emergencyData?.birthDay && (
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-2">
                !
              </span>
            )}
          </div>
        </div>
        {(errors.emergencyData?.birthYear ||
          errors.emergencyData?.birthMonth ||
          errors.emergencyData?.birthDay) && (
          <div className="text-red-500 text-sm mt-1">
            {errors.emergencyData.birthYear?.message?.toString() ||
              errors.emergencyData.birthMonth?.message?.toString() ||
              errors.emergencyData.birthDay?.message?.toString()}
          </div>
        )}
      </div>

      {/* 性別 */}
      <div className="mb-4">
        <label className={labelStyles}>
          {useEnglish ? 'Gender' : '性別'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-1 flex space-x-4">
          <label className="flex items-center text-sm">
            <input
              type="radio"
              value="male"
              className={`mr-2 ${errors.emergencyData?.EmergencyGender ? 'border-red-500' : ''}`}
              {...register('emergencyData.EmergencyGender', {
                required: useEnglish
                  ? 'Gender is required'
                  : '性別が未入力です',
              })}
            />
            {useEnglish ? 'Male' : '男性'}
          </label>
          <label className="flex items-center text-sm">
            <input
              type="radio"
              value="female"
              className={`mr-2 ${errors.emergencyData?.EmergencyGender ? 'border-red-500' : ''}`}
              {...register('emergencyData.EmergencyGender', {
                required: useEnglish
                  ? 'Gender is required'
                  : '性別が未入力です',
              })}
            />
            {useEnglish ? 'Female' : '女性'}
          </label>
        </div>
        {errors.emergencyData?.EmergencyGender && (
          <div className="text-red-500 text-sm mt-1">
            {errors.emergencyData.EmergencyGender.message?.toString()}
          </div>
        )}
      </div>

      {/* 電話番号 */}
      <div className="mb-5">
        <label className={labelStyles}>
          {useEnglish ? 'Phone Number' : '電話番号'}{' '}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-1">
          <Controller
            name="emergencyData.phoneNumber"
            control={control}
            rules={{
              required: useEnglish
                ? 'Phone Number is required'
                : '緊急連絡先の電話番号が未入力です',
            }}
            render={({ field }) => <PhoneCodeSelect {...field} />}
          />
          {errors.emergencyData?.phoneNumber && (
            <>
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pr-3">
                !
              </span>
              <div className="text-red-500 text-sm mt-1">
                {errors.emergencyData.phoneNumber.message?.toString()}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
